import {chain, sortBy, unionBy} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {intersperseNodes} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {filterRenderers} from '../../../analytics/helpers/analyticsFiltersHelpers';
import {buildTeammateDisplayName, teammateCanAccessInboxes} from '../../../helpers/modelHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {ApiInboxes, InboxModel} from '../../../models/api/inboxModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../services/http/http';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {DropdownItemTeammateAvatar} from '../../common/dropdowns/dropdownItemTeammateAvatar';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormMultiselectAssigneesProps extends LensFormMultiselectProps<TeammateModel> {
  selectedInboxIds?: ReadonlyArray<number>;
  shouldFilterTeammatesByInboxAccess?: boolean;
  selectedInboxesInState: ReadonlyArray<InboxModel>;
}

/*
 * Style.
 */

const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${ellipsis()}
`;

/*
 * Component.
 */

export const LensFormMultiSelectAssignees: FC<LensFormMultiselectAssigneesProps> = (props) => {
  const {
    isDisabled,
    choices,
    selectedIds,
    onSetItemIds,
    selectedInboxIds,
    selectedInboxesInState,
    shouldFilterTeammatesByInboxAccess,
  } = props;
  const companyUrl = useAppSelector(getCompanyUrl);
  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(
      choices,
      selectedIds,
      companyUrl,
      selectedInboxesInState,
      selectedInboxIds,
      shouldFilterTeammatesByInboxAccess,
    );
    return computeFilterDropdownSourceAsync(fetcher);
  }, [
    choices,
    companyUrl,
    shouldFilterTeammatesByInboxAccess,
    selectedIds,
    selectedInboxIds,
    selectedInboxesInState,
  ]);

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={onSetItemIds}
      onClearAll={() => onSetItemIds([])}
      renderValue={() => renderValue(props)}
      renderPlaceholder={() =>
        props.placeholder || <Trans id="lens-form-multiselect-assignees-placeholder">Select assignees</Trans>
      }
      label={
        props.label || (
          <Trans id="lens-form-multiselect-assignees-label">Filtered by any of these assignees</Trans>
        )
      }
      dropdownTitle={<Trans id="lens-form-multiselect-assignees-dropdown-title">Teammates</Trans>}
      isDisabled={isDisabled}
      isRequired={props.isRequired}
      messageForDisabled={
        <Trans id="lens-form-multiselect-missing-inbox-message">
          Field will be enabled once an inbox is selected.
        </Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );
};

/*
 * Helpers.
 */

const filterRowsByQuery = memoize(20)((teammates: ReadonlyArray<TeammateModel>, searchQuery: string) => {
  // For empty query, render all choices.
  if (!searchQuery) {
    return teammates;
  }

  // Otherwise, filter by name.
  const lowerCaseQuery = searchQuery.toLowerCase();
  return (
    chain(teammates)
      // TODO-LENS: consider using localeCompare method instead. Based on makeFilterRows<T> which compare like this does.
      .filter((teammate) => buildTeammateDisplayName(teammate).toLowerCase().includes(lowerCaseQuery))
      .value()
  );
});

function renderValue(props: LensFormMultiselectAssigneesProps) {
  const {selectedIds, choices} = props;

  if (!selectedIds.length) {
    return null;
  }

  const selectedTeammates = chain(selectedIds)
    .map((id) => choices.find((teammate) => teammate.id === id))
    .compact()
    .value();

  return (
    <StyledValueWrapper>
      {intersperseNodes(
        selectedTeammates.map((teammate) => ({
          node: filterRenderers[AnalyticsFilterTypesEnum.TEAMMATES].renderItem(teammate),
          key: String(teammate.id),
        })),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}

const buildRowGenerator: FilterRowGenerator<TeammateModel> = (
  teammate,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isSelected = dropdownSelectedIds.includes(teammate.id);

  return {
    isSelected,
    renderer: () => (
      <>
        <DropdownItemTeammateAvatar teammate={teammate} />
        <DropdownItemContent isChecked={isSelected}>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
      </>
    ),
    onSelect: makeOnItemSelect(teammate.id),
    id: teammate.id,
  };
};

function buildFetcher(
  choices: ReadonlyArray<TeammateModel>,
  initialSelectedIds: ReadonlyArray<number>,
  companyUrl: string,
  selectedInboxesInState: ReadonlyArray<InboxModel>,
  selectedInboxIds?: ReadonlyArray<number>,
  shouldFilterTeammatesByInboxAccess?: boolean,
): DropdownFetcher<TeammateModel, {selectedInboxes: ReadonlyArray<InboxModel>}> {
  return async (query, _items, cancelToken, paginationData) => {
    let newPaginationData = {selectedInboxes: selectedInboxesInState};
    /** Helper function to fetch inboxes if we don't have all selected inboxes in state */
    const getAllSelectedInboxes = async () => {
      // This is a hack - we store the previously fetched inboxes in the pagination data to pass to future
      // requests. Avoids us needing to hit the API if someone is searching for a teammate by name and we already
      // have all the inboxes
      const previouslyFetchedSelectedInboxes = paginationData?.selectedInboxes ?? [];
      const allSelectedInboxes = unionBy(previouslyFetchedSelectedInboxes, selectedInboxesInState, 'id');
      if (!selectedInboxIds || allSelectedInboxes.length === selectedInboxIds.length) {
        // We don't need to fetch anything, just use the inboxes in state
        return allSelectedInboxes;
      }

      // Fetch all the inboxes and filter the teammates by them. Useful for the case where
      // an admin is creating a view and might not have all the inboxes in state.
      const url = `${companyUrl}/inboxes/?ids=${selectedInboxIds.join(',')}`;

      const inboxes = await fetchApiAsync(ApiInboxes, url, {
        cancelToken,
      });

      return inboxes;
    };

    const getFilteredTeammates = async () => {
      if (!shouldFilterTeammatesByInboxAccess) {
        return choices;
      }

      // Fetch the selected inboxes.
      const selectedInboxes = await getAllSelectedInboxes();
      newPaginationData = {selectedInboxes};

      // Filter the teammates based on their access to the selected inboxes.
      const accessFilteredTeammates =
        selectedInboxes.length > 0
          ? choices.filter((teammate) => teammateCanAccessInboxes(teammate, selectedInboxes))
          : choices;

      return accessFilteredTeammates;
    };

    // Fetch the filtered inboxes.
    const accessFilteredTeammates = await getFilteredTeammates();

    // Filter the teammates by which ones match our query
    const items = filterRowsByQuery(accessFilteredTeammates, query);

    return {
      items:
        initialSelectedIds.length > 0 ? sortBy(items, (row) => !initialSelectedIds.includes(row.id)) : items,
      canLoadMore: false,
      paginationData: newPaginationData,
    };
  };
}
